import CustomSelect from "components/customs/dropdown";
import React, { useEffect, useState } from "react";
import { Col, Row, Spinner } from "reactstrap";
import {
  fetchAllAgencies,
  fetchAllAgencyProjects,
} from "services/services.projects";
import { Toast, getFormattedOptions } from "utility/helpers";
import { fetchRawReport } from "services/services.summary";
import { fetchAllRegions } from "services/services.congressman";
import "jspdf-autotable";
import moment from "moment";
import { HeaderText, AddButton } from "assets/js/Styles";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import styled from "styled-components";
import { ALL_ALLOCATION_STATUS_OPTIONS } from "constants/status";
import { ACCOUNT_AGENCY, ALL_OPTION } from "utility/constants";
import { addProjectDropdownStyles } from "constants/style";
import FormGenerator from "components/customs/form-generator/input";
import { generateExcel } from "./utility";

const Container = styled.div`
  margin-top: 24px;
  padding-bottom: 4rem;
`;

const StyledButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonFormBlue};
  width: 130px;
`;

const HeaderSection = styled(Row)`
  display: flex;
  margin: 0;
  align-items: flex-end;
`;

const FormSection = styled.div`
  margin-top: 44px;
`;

const FormRow = styled(Row)`
  display: flex;
  margin: 0;
  justify-content: space-between;
  margin-top: 1rem;
`;

const FormLabel = styled.h5`
  font-weight: bold;
`;

const FormActionsSections = styled(Row)`
  display: flex;
  margin: 0;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const DateSection = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
`;

const SummaryReportTab = () => {
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedRecipients, setSelectRecipients] = useState(null);
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statuses, setStatuses] = useState(ALL_ALLOCATION_STATUS_OPTIONS);

  const { user } = useAuth();
  const role = user?.type;

  const [selectOptions, setSelectOptions] = useState({
    agency: [],
    project: [],
    recipient: [],
  });

  const handleExportBtnClicked = () => {
    setIsLoading(true);

    const projectIds = selectedProject.some(
      (project) => project.value === ALL_OPTION
    )
      ? selectOptions.project?.slice(1)?.map((project) => project.id)
      : selectedProject?.map((project) => project.id) ?? [];

    const selectedStatues = selectedStatus.some(
      (status) => status.value === ALL_OPTION
    )
      ? ALL_OPTION
      : selectedStatus?.map((status) => status.value) ?? [];

    const selectedRegions = selectedRecipients.some(
      (status) => status.value === ALL_OPTION
    )
      ? ALL_OPTION
      : selectedRecipients.map((recipient) => recipient.label);

    const params = {
      projectIds:
        projectIds === ALL_OPTION ? projectIds : JSON.stringify(projectIds),
      status:
        selectedStatues === ALL_OPTION
          ? selectedStatues
          : JSON.stringify(selectedStatues),
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).format("YYYY-MM-DD"),
      receivingEntities:
        selectedRegions === ALL_OPTION
          ? selectedRegions
          : JSON.stringify(selectedRegions),
    };

    fetchRawReport({
      params,
      callback: ({ isSuccess, data }) => {
        setIsLoading(false);

        if (isSuccess && data.length !== 0) {
          generateExcel(data);
        } else if (data.length === 0) {
          Toast({
            type: 4,
            content: "Empty data",
          });
        } else {
          Toast({
            type: 4,
            content: "An error occurred!",
          });
        }
      },
    });
  };

  const fetchAgencies = () => {
    fetchAllAgencies({
      callback: (data) => {
        const newData = getFormattedOptions("name", data);

        setAgencyOptions(newData);

        if (role === ACCOUNT_AGENCY) {
          setSelectedAgency(newData);
        } else {
          newData.unshift({
            value: ALL_OPTION,
            label: "All",
          });
        }

        setSelectOptions({ ...selectOptions, agency: newData });
      },
    });
  };

  const fetchAgencyProjects = (url) => {
    return new Promise((resolve, reject) => {
      fetchAllAgencyProjects({
        url,
        callback: (data) => {
          resolve(data);
        },
        errorCallback: (error) => {
          reject(error);
        },
      });
    });
  };

  const fetchProjects = async () => {
    try {
      let options = [];

      if (selectedAgency.some((agency) => agency.value === ALL_OPTION)) {
        const data = await fetchAgencyProjects("/project");
        options = getFormattedOptions("name", data);
      } else {
        const agencies = selectedAgency.map((agency) => agency.id).toString();

        await fetchAgencyProjects(`/project/?agencies=${agencies}`).then(
          (data) => {
            const newData = getFormattedOptions("name", data);
            options.push(...newData);
          }
        );
      }

      if (role !== "Agency" && options.length !== 1) {
        options?.unshift({
          value: ALL_OPTION,
          label: "All",
        });
      } else {
        setSelectedProject(options);
      }

      setProjectOptions(options);

      setSelectOptions({
        ...selectOptions,
        project: options,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const fetchRegions = () => {
    fetchAllRegions({
      callback: (data) => {
        const newData = getFormattedOptions("acronym", data);

        newData.unshift(
          {
            value: ALL_OPTION,
            label: "All",
          },
          {
            value: "National",
            label: "National",
          }
        );

        setRecipientOptions(newData);
        setSelectOptions({ ...selectOptions, recipient: newData });
      },
    });
  };

  const isBtnDisabled =
    isLoading ||
    selectedProject === null ||
    selectedAgency === null ||
    selectedStatus === null ||
    startDate === null ||
    endDate === null ||
    selectedRecipients === null;

  useEffect(() => {
    //  fetch all agencies
    fetchAgencies();
  }, []);

  useEffect(() => {
    if (selectedAgency !== null) {
      // reset selected project on agency change
      setSelectedProject(null);
      setProjectOptions([]);
      // reset selected recipients on agency change
      setSelectRecipients(null);
      setRecipientOptions([]);
      // fetch projects belonging to the selected agency
      fetchProjects();
    }

    if (
      selectedAgency !== null &&
      selectedAgency?.some((agency) => agency.value === ALL_OPTION)
    ) {
      setAgencyOptions([]);
    }
  }, [selectedAgency]);

  useEffect(() => {
    if (selectedProject !== null) {
      // reset selected recipients on agency change
      setSelectRecipients(null);

      // fetch congs belonging to selected agency and project
      if (recipientOptions.length === 0) fetchRegions();
    }

    if (
      selectedProject !== null &&
      selectedProject?.some((project) => project.value === ALL_OPTION)
    ) {
      setProjectOptions([]);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (
      selectedStatus !== null &&
      selectedStatus?.some((status) => status.value === ALL_OPTION)
    ) {
      setStatuses([]);
    }
  }, [selectedStatus]);

  useEffect(() => {
    if (
      selectedRecipients !== null &&
      selectedRecipients?.some((recipient) => recipient.value === ALL_OPTION)
    ) {
      setRecipientOptions([]);
    }
  }, [selectedRecipients]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Container>
      <HeaderSection>
        <HeaderText>{"Summary Report"}</HeaderText>
      </HeaderSection>
      <FormSection>
        <FormRow>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <FormLabel>{"Agency"}</FormLabel>
            <p>{"Provide the name of the agency"}</p>
          </Col>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <CustomSelect
              placeholder="Agency"
              styles={addProjectDropdownStyles}
              options={agencyOptions}
              value={selectedAgency}
              onChange={(option) => {
                const index = option.length - 1;

                if (option[index]?.value === ALL_OPTION) {
                  setSelectedAgency([option[index]]);
                } else {
                  setSelectedAgency(option);
                }

                if (option?.length === 0 && agencyOptions?.length === 0) {
                  setAgencyOptions(selectOptions.agency);
                }
              }}
              isMulti
              filterOption={(option, searchValue) =>
                option?.label
                  ?.toLowerCase()
                  ?.includes(searchValue?.toLowerCase()) ||
                option?.data?.acronym
                  ?.toLowerCase()
                  ?.includes(searchValue?.toLowerCase())
              }
              isDisabled={role === ACCOUNT_AGENCY}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <FormLabel>{"Program"}</FormLabel>
            <p>{"Provide the name of the program"}</p>
          </Col>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <CustomSelect
              placeholder="Program"
              styles={addProjectDropdownStyles}
              options={projectOptions}
              value={selectedProject}
              onChange={(option) => {
                const index = option.length - 1;

                if (option[index]?.value === ALL_OPTION) {
                  setSelectedProject([option[index]]);
                } else {
                  setSelectedProject(option);
                }

                if (option?.length === 0 && projectOptions?.length === 0) {
                  setProjectOptions(selectOptions.project);
                }
              }}
              isMulti
              isDisabled={selectedAgency === null}
              filterOption={(option, searchValue) =>
                option?.label
                  ?.toLowerCase()
                  ?.includes(searchValue?.toLowerCase())
              }
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <FormLabel>{"Date"}</FormLabel>
            <p>{"Provide the date of implementation"}</p>
          </Col>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <DateSection>
              <FormGenerator
                type="datetime"
                selected={startDate}
                placeholderText="Start Date"
                dateFormat="MMM-dd-yyyy"
                onChange={(date) => {
                  setStartDate(date);
                }}
              />

              <FormGenerator
                type="datetime"
                selected={endDate}
                placeholderText="End Date"
                dateFormat="MMM-dd-yyyy"
                maxDate={new Date()}
                onChange={(date) => {
                  setEndDate(date);
                }}
              />
            </DateSection>
          </Col>
        </FormRow>
        <FormRow>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <FormLabel>{"Status"}</FormLabel>
            <p>{"Provide the status of the program"}</p>
          </Col>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <CustomSelect
              placeholder="Status"
              styles={addProjectDropdownStyles}
              options={statuses}
              value={selectedStatus}
              onChange={(option) => {
                const index = option.length - 1;

                if (option[index]?.value === ALL_OPTION) {
                  setSelectedStatus([option[index]]);
                } else {
                  setSelectedStatus(option);
                }

                if (option?.length === 0 && statuses?.length === 0) {
                  setStatuses(ALL_ALLOCATION_STATUS_OPTIONS);
                }
              }}
              isMulti
              isDisabled={selectedProject === null}
              filterOption={(option, searchValue) =>
                option?.label
                  ?.toLowerCase()
                  ?.includes(searchValue?.toLowerCase())
              }
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <FormLabel>{"Recipient"}</FormLabel>
            <p>{"Provide the recipient of the program"}</p>
          </Col>
          <Col
            xs="12"
            sm="6"
            md="6"
          >
            <CustomSelect
              placeholder="Recipient"
              styles={addProjectDropdownStyles}
              options={recipientOptions}
              value={selectedRecipients}
              onChange={(option) => {
                const index = option.length - 1;

                if (option[index]?.value === ALL_OPTION) {
                  setSelectRecipients([option[index]]);
                } else {
                  setSelectRecipients(option);
                }

                if (option?.length === 0 && recipientOptions?.length === 0) {
                  setRecipientOptions(selectOptions.recipient);
                }
              }}
              isMulti
              isDisabled={selectedProject === null}
              maxMenuHeight="150px"
              filterOption={(option, searchValue) =>
                option?.label
                  ?.toLowerCase()
                  ?.includes(searchValue?.toLowerCase())
              }
            />
          </Col>
        </FormRow>
        <FormActionsSections>
          <Col xs="auto">
            <StyledButton
              onClick={() => handleExportBtnClicked()}
              disabled={isBtnDisabled}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Export Report"
              )}
            </StyledButton>
          </Col>
        </FormActionsSections>
      </FormSection>
    </Container>
  );
};

export default SummaryReportTab;
